import { Box } from '@mui/material'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { PlotCellImage } from '../PlotCellImage'
import { PlotCellRawImage } from '../PlotCellRawImage'
import { readableTextColor, useLabelColors } from './labelColors'

const PAGE_SIZE = 120
const TILE_SIZE = 80
const SELECTED_BORDER = '3px solid #7c3aed'
const UNSELECTED_BORDER = '3px solid transparent'
// Pointer travel (px) before a press becomes a drag-select instead of a click
const DRAG_THRESHOLD = 10

interface SelectableCellProps {
  cellId: string
  selected: boolean
  label?: string
  labelColor?: string
  showRawCrop?: boolean
  onToggle: (cellId: string) => void
}

// Memoized so toggling/labeling one cell only re-renders that cell, not the wall.
const SelectableCell = memo(
  ({ cellId, selected, label, labelColor, showRawCrop, onToggle }: SelectableCellProps) => {
    let border = UNSELECTED_BORDER
    if (selected) {
      border = SELECTED_BORDER
    } else if (label && labelColor) {
      border = `3px solid ${labelColor}`
    }

    const masked = (
      <PlotCellImage
        cellId={cellId}
        revertToDefaultDimensions={false}
        imgProps={{ width: TILE_SIZE, height: TILE_SIZE }}
        wrapperProps={{ sx: { p: 0 } }}
      />
    )

    return (
      <Box
        data-cell-id={cellId}
        onClick={() => onToggle(cellId)}
        data-testid="labeling-cell"
        sx={{ position: 'relative', cursor: 'pointer', lineHeight: 0, border }}
      >
        {showRawCrop ? (
          // Masked (left) + raw (right), framed as one pair with a hairline divider
          <Box
            sx={{
              display: 'flex',
              gap: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              border: '1px solid rgba(0, 0, 0, 0.45)',
              borderRadius: '3px',
              overflow: 'hidden',
            }}
          >
            {masked}
            <PlotCellRawImage
              cellId={cellId}
              enabled
              imgProps={{ width: TILE_SIZE, height: TILE_SIZE }}
              wrapperProps={{ sx: { p: 0 } }}
            />
          </Box>
        ) : (
          masked
        )}
        {label ? (
          <Box
            sx={{
              position: 'absolute',
              left: '3px',
              bottom: '3px',
              px: '4px',
              borderRadius: '3px',
              fontSize: '8px',
              lineHeight: 1.5,
              letterSpacing: '0.3px',
              color: readableTextColor(labelColor ?? '#7c3aed'),
              backgroundColor: labelColor ?? '#7c3aed',
            }}
          >
            {label}
          </Box>
        ) : null}
      </Box>
    )
  }
)
SelectableCell.displayName = 'SelectableCell'

interface MarqueeRect {
  left: number
  top: number
  width: number
  height: number
}

interface LabelingGridProps {
  /** cellIds for the currently selected cluster */
  cellIds: string[]
  /** Show the raw crop beside the masked crop (internal, allowlisted runs only) */
  showRawCrop?: boolean
  /** Allow marquee/drag to select already-labeled cells too (vetting flow) */
  selectLabeled?: boolean
}

/**
 * Renders the wall of cell images for a cluster. Clicking a cell toggles its
 * selection; dragging over a region selects the cells it covers (unlabeled only,
 * unless selectLabeled is set). Images lazy-load per tile and the visible set
 * grows on scroll.
 */
export const LabelingGrid = ({
  cellIds,
  showRawCrop,
  selectLabeled,
}: LabelingGridProps): JSX.Element => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const { ref, inView } = useInView()
  const {
    labeling: { selectedCellIds, labels },
    toggleCellSelection,
    toggleCellsInRegion,
  } = useLabelingSlice()
  const labelColors = useLabelColors()

  const containerRef = useRef<HTMLDivElement>(null)
  const dragStart = useRef<{ x: number; y: number } | null>(null)
  const draggedRef = useRef(false)
  const marqueeRef = useRef<MarqueeRect | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null)

  // Reset the window whenever the cluster (cell set) changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [cellIds])

  useEffect(() => {
    if (inView && visibleCount < cellIds.length) {
      setVisibleCount((count) => Math.min(count + PAGE_SIZE, cellIds.length))
    }
  }, [inView, visibleCount, cellIds.length])

  const handleToggle = useCallback(
    (cellId: string) => toggleCellSelection(cellId),
    [toggleCellSelection]
  )

  // While a drag is active, track the marquee and, on release, select every
  // cell tile the marquee overlaps. Listeners mount once per drag.
  useEffect(() => {
    if (!dragActive) return undefined

    const handleMove = (event: MouseEvent) => {
      const start = dragStart.current
      if (!start) return
      const dx = event.clientX - start.x
      const dy = event.clientY - start.y
      if (!draggedRef.current && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return
      draggedRef.current = true
      const rect: MarqueeRect = {
        left: Math.min(start.x, event.clientX),
        top: Math.min(start.y, event.clientY),
        width: Math.abs(dx),
        height: Math.abs(dy),
      }
      marqueeRef.current = rect
      setMarquee(rect)
    }

    const handleUp = () => {
      const rect = marqueeRef.current
      if (draggedRef.current && rect && containerRef.current) {
        const bounds = {
          left: rect.left,
          top: rect.top,
          right: rect.left + rect.width,
          bottom: rect.top + rect.height,
        }
        const ids: string[] = []
        containerRef.current.querySelectorAll<HTMLElement>('[data-cell-id]').forEach((el) => {
          const r = el.getBoundingClientRect()
          // Select a cell only when the marquee covers its center, so barely
          // grazing the edge of a neighboring row does not pick it up.
          const centerX = r.left + r.width / 2
          const centerY = r.top + r.height / 2
          const covered =
            centerX >= bounds.left &&
            centerX <= bounds.right &&
            centerY >= bounds.top &&
            centerY <= bounds.bottom
          const id = el.getAttribute('data-cell-id')
          if (covered && id) ids.push(id)
        })
        if (ids.length > 0) toggleCellsInRegion({ cellIds: ids, includeLabeled: selectLabeled })
      }
      dragStart.current = null
      marqueeRef.current = null
      setMarquee(null)
      setDragActive(false)
    }

    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [dragActive, toggleCellsInRegion, selectLabeled])

  const handleMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0) return
    dragStart.current = { x: event.clientX, y: event.clientY }
    draggedRef.current = false
    marqueeRef.current = null
    setDragActive(true)
  }

  // Suppress the click that follows a real drag so it does not toggle a cell
  const handleClickCapture = (event: React.MouseEvent) => {
    if (draggedRef.current) {
      event.stopPropagation()
      draggedRef.current = false
    }
  }

  return (
    <Box ref={containerRef} onMouseDown={handleMouseDown} onClickCapture={handleClickCapture}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: showRawCrop ? '8px' : '1px',
          userSelect: 'none',
        }}
        data-testid="labeling-grid"
      >
        {cellIds.slice(0, visibleCount).map((cellId) => (
          <SelectableCell
            key={cellId}
            cellId={cellId}
            selected={!!selectedCellIds[cellId]}
            label={labels[cellId]}
            labelColor={labels[cellId] ? labelColors[labels[cellId]] : undefined}
            showRawCrop={showRawCrop}
            onToggle={handleToggle}
          />
        ))}
      </Box>
      {visibleCount < cellIds.length ? <Box ref={ref} sx={{ height: '24px' }} /> : null}
      {marquee && (marquee.width > 0 || marquee.height > 0) ? (
        <Box
          sx={{
            position: 'fixed',
            left: `${marquee.left}px`,
            top: `${marquee.top}px`,
            width: `${marquee.width}px`,
            height: `${marquee.height}px`,
            border: '1px solid #7c3aed',
            backgroundColor: 'rgba(124, 58, 237, 0.15)',
            pointerEvents: 'none',
            zIndex: 1200,
          }}
        />
      ) : null}
    </Box>
  )
}

export default LabelingGrid
